import { useMemo } from "react";
import { useFilterOptions } from "@/lib/api/generated";
import type { HighlightOptionSchema } from "@/lib/api/model";
import { HighlightType, HIGHLIGHT_FRAMES } from "@/features/candidate/lib/highlights";
import { isLegacyClearanceHighlight } from "@/features/search/lib/clearances";

export interface HighlightEntry {
  type: string;
  icon: string;
  label: string;
  description: string;
  category: string;
  color: string;
  /** Light-mode legible variant; consumers fall back to `color`. */
  lightColor?: string;
}

const FALLBACK_FRAME = {
  icon: "IconStar",
  color: "#d1d5db",
  gradientRgb: "209,213,219",
  lightColor: "#4b5563",
  label: "",
};

function frameFor(id: string) {
  return HIGHLIGHT_FRAMES[id as HighlightType] ?? FALLBACK_FRAME;
}

function toEntry(h: HighlightOptionSchema): HighlightEntry {
  const frame = frameFor(h.id);
  return {
    type: h.id,
    icon: frame.icon,
    label: frame.label || h.name,
    description: h.description,
    category: h.category,
    color: frame.color,
    lightColor: frame.lightColor ?? frame.color,
  };
}

function labelFromId(id: string): string {
  return id.replace(/[_-]+/g, " ").trim() || id;
}

export function resolveHighlightEntry(
  id: string,
  entries: readonly HighlightEntry[],
): HighlightEntry {
  const entry = entries.find((candidate) => candidate.type === id);
  if (entry) return entry;

  const frame = frameFor(id);
  return {
    type: id,
    icon: frame.icon,
    label: frame.label || labelFromId(id),
    description: "",
    category: "Hidden",
    color: frame.color,
    lightColor: frame.lightColor ?? frame.color,
  };
}

export function resolveHighlightEntries(
  ids: readonly string[],
  entries: readonly HighlightEntry[],
): HighlightEntry[] {
  return ids.map((id) => resolveHighlightEntry(id, entries));
}

/**
 * Load the highlight catalog from the backend (`/api/search/filter-options`).
 *
 * Backend is the source of truth. Falls back to the statically-registered
 * `HIGHLIGHT_FRAMES` only for icons/colors — labels, descriptions, and the set
 * of available IDs come from the API response.
 */
export function useHighlightEntries(): {
  entries: HighlightEntry[];
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useFilterOptions({
    query: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  });

  const entries = useMemo<HighlightEntry[]>(() => {
    if (!data?.highlights?.length) return [];
    return data.highlights
      .filter((h) => !isLegacyClearanceHighlight(h.id))
      .map(toEntry);
  }, [data?.highlights]);

  return { entries, isLoading, error };
}
